/**
 * useDocumentTypeNarrowing — zawężenie listy dokumentów do wybranych typów, złożone
 * z TRZECH źródeł (Etap 3, blok C — konsumpcja zawężenia na listach `SpecialDocs`/
 * `InternalDocs`, blok B dostarczył sam kontrakt filtra na pozycji menu):
 *
 *   1. URL `?documentTypeIds=<CSV>` — kontrakt filtra pozycji menu (patrz
 *      `DOCUMENT_TYPE_FILTER_FIELD` / `installed-menu.ts`), wiele typów naraz.
 *   2. URL `?documentTypeId=<uuid>` — legacy pojedynczy parametr (stare linki menu, np.
 *      moduł Urlopy/Teczka HR), wsteczna zgodność.
 *   3. `useActiveMenuItem()?.view?.baseFilters` — filtr stały `document_type_id in […]`
 *      zapisany na pozycji menu przez edytor filtrów stałych (żyje w konfiguracji
 *      pozycji menu, nie w URL-u) — `extractDocumentTypeIdsFromBaseFilters` (`@saas/shared`).
 *
 * Wynik to DEDUPLIKOWANA unia wszystkich trzech źródeł — pusta tablica oznacza brak
 * zawężenia (lista pokazuje wszystkie typy klasy). Backend (`parseDocumentTypeIds`,
 * `apps/api/lib/documents/document-type-filter.ts`) łączy analogicznie `documentTypeIds`
 * (CSV) + `documentTypeId` (legacy) po stronie URL-a żądania — front wysyła zawsze CSV pod
 * `?documentTypeIds=`, więc backend widzi jedno źródło zamiast trzech.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { extractDocumentTypeIdsFromBaseFilters } from '@saas/shared';

import { useActiveMenuItem } from '@/hooks/useActiveMenuItem';

export function useDocumentTypeNarrowing(): string[] {
  const [searchParams] = useSearchParams();
  const activeMenuItem = useActiveMenuItem();

  const idsCsv = searchParams.get('documentTypeIds');
  const legacyId = searchParams.get('documentTypeId');
  const baseFilters = activeMenuItem?.view?.baseFilters;

  return useMemo(() => {
    const out = new Set<string>();

    if (idsCsv) {
      for (const raw of idsCsv.split(',')) {
        const trimmed = raw.trim();
        if (trimmed.length > 0) out.add(trimmed);
      }
    }

    if (legacyId) {
      const trimmed = legacyId.trim();
      if (trimmed.length > 0) out.add(trimmed);
    }

    for (const id of extractDocumentTypeIdsFromBaseFilters(baseFilters)) {
      out.add(id);
    }

    return [...out];
  }, [idsCsv, legacyId, baseFilters]);
}
